import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { InvoiceLineItem } from '@models/invoice/invoice-line-item';
import { InvoiceLineItemDiscountType } from '@models/invoice/invoice-line-item-discout-type';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

enum DiscountMode {
  None,
  Discount,
  Additional,
}

@Component({
  selector: 'app-add-credit',
  templateUrl: './add-credit.component.html',
  styleUrls: ['./add-credit.component.less'],
})
export class AddCreditComponent implements OnInit {
  @Input() creditLineItem?: InvoiceLineItem;
  isEditMode = false;
  isSwapping = false;
  isSwapped = false;
  previousDiscountMode: DiscountMode | null = null;
  editForm: FormGroup = new FormGroup({
    creditAmount: new FormControl(0, [Validators.required]),
    paymentAmount: new FormControl({ value: 0, disabled: true }),
    discountMode: new FormControl(DiscountMode.None),
    isPercentDiscount: new FormControl({ value: true, disabled: true }),
    discountValue: new FormControl({ value: 0, disabled: true }),
  });
  DiscountMode = DiscountMode;
  private readonly swapDelay = 200;

  get creditAmount() {
    return this.editForm.get('creditAmount')?.value;
  }

  get paymentAmount() {
    return this.editForm.get('paymentAmount')?.value;
  }

  get discountValue() {
    return this.editForm.get('discountValue')?.value;
  }

  get isPercentDiscount() {
    return this.editForm.get('isPercentDiscount')?.value;
  }

  get discountMode() {
    return this.editForm.get('discountMode')?.value;
  }
  
  get discountType() {
    if (this.isPercentDiscount && this.discountMode === DiscountMode.Discount) {
      return InvoiceLineItemDiscountType.Percent;
    }
    if (this.isPercentDiscount && this.discountMode === DiscountMode.Additional) {
      return InvoiceLineItemDiscountType.PercentAdditional;
    }
    if (!this.isPercentDiscount && this.discountMode === DiscountMode.Discount) {
      return InvoiceLineItemDiscountType.Amount;
    }
    if (!this.isPercentDiscount && this.discountMode === DiscountMode.Additional) {
      return InvoiceLineItemDiscountType.AmountAdditional;
    }
    return null;
  }

  constructor(public activeModal: NgbActiveModal) {}

  ngOnInit() {
    if (this.creditLineItem) {
      this.isEditMode = true;
      this.populateFormForEdit();
    } else {
      this.creditLineItem = new InvoiceLineItem();
    }
    this.initFormChangeSubscriptions();
  }

  private populateFormForEdit() {
    if (this.creditLineItem) {
      this.editForm.patchValue({
        creditAmount: this.creditLineItem.unitPrice,
        discountValue: this.creditLineItem.discountPrice,
        isPercentDiscount:
          this.creditLineItem.discountType === InvoiceLineItemDiscountType.Percent ||
          this.creditLineItem.discountType === InvoiceLineItemDiscountType.PercentAdditional,
        totalAmount: this.creditLineItem.total,
        paymentAmount: this.creditLineItem.total,
      });
    }

    if (
      this.creditLineItem.discountType == InvoiceLineItemDiscountType.Percent ||
      this.creditLineItem.discountType == InvoiceLineItemDiscountType.Amount
    ) {
      this.editForm.get('discountMode')?.setValue(DiscountMode.Discount);
      this.previousDiscountMode = DiscountMode.Discount;
    }

    if (
      this.creditLineItem.discountType == InvoiceLineItemDiscountType.PercentAdditional ||
      this.creditLineItem.discountType == InvoiceLineItemDiscountType.AmountAdditional
    ) {
      this.editForm.get('discountMode')?.setValue(DiscountMode.Additional);
      this.previousDiscountMode = DiscountMode.Additional;
      this.isSwapped = true;
    }

    this.editForm.get('isPercentDiscount')?.enable();
    this.editForm.get('discountValue')?.enable();
    this.editForm.get('paymentAmount')?.enable();
  }

  private initFormChangeSubscriptions() {
    this.editForm.get('discountMode')?.valueChanges.subscribe(async (value) => {
      if (this.discountMode === DiscountMode.Additional || this.previousDiscountMode === DiscountMode.Additional) {
        this.isSwapping = true;
        await this.delay(this.swapDelay);
        this.swapCreditAndPaymentAmounts();
      }

      if (value === DiscountMode.None) {
        this.editForm.get('isPercentDiscount')?.disable({ emitEvent: false });
        this.editForm.get('discountValue')?.disable({ emitEvent: false });
        this.editForm.get('paymentAmount')?.disable({ emitEvent: false });
        this.calculatePaymentAmount();
      } else {
        this.editForm.get('isPercentDiscount')?.enable({ emitEvent: false });
        this.editForm.get('discountValue')?.enable({ emitEvent: false });
        this.editForm.get('paymentAmount')?.enable({ emitEvent: false });
      }

      if (this.discountMode === DiscountMode.Discount) {
        this.calculatePaymentAmount();
      }

      if (this.discountMode === DiscountMode.Additional) {
        this.calculateCreditAmount();
      }

      this.previousDiscountMode = value;

      if (this.isSwapping) {
        await this.delay(this.swapDelay);
        this.isSwapping = false;
      }
    });

    this.editForm.get('isPercentDiscount')?.valueChanges.subscribe((value) => {
      if (this.discountMode == DiscountMode.Discount) {
        this.calculatePaymentAmount();
      }
      if (this.discountMode === DiscountMode.Additional) {
        this.calculateCreditAmount();
      }
    });

    this.editForm.get('discountValue')?.valueChanges.subscribe((value) => {
      if (this.discountMode == DiscountMode.Discount) {
        this.calculatePaymentAmount();
      }
      if (this.discountMode === DiscountMode.Additional) {
        this.calculateCreditAmount();
      }
    });

    this.editForm.get('creditAmount')?.valueChanges.subscribe((value) => {
      this.calculatePaymentAmount();
    });

    this.editForm.get('paymentAmount')?.valueChanges.subscribe((value) => {
      this.calculateCreditAmount();
    });
  }

  private swapCreditAndPaymentAmounts() {
    const creditAmount = this.editForm.get('creditAmount')?.value;
    const paymentAmount = this.editForm.get('paymentAmount')?.value;
    this.editForm.get('creditAmount')?.setValue(paymentAmount, { emitEvent: false });
    this.editForm.get('paymentAmount')?.setValue(creditAmount, { emitEvent: false });
    this.isSwapped = this.discountMode === DiscountMode.Additional;
  }

  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private calculatePaymentAmount() {
    if (this.discountMode === DiscountMode.None) {
      this.editForm.get('paymentAmount')?.setValue(this.creditAmount, { emitEvent: false });
    } else {
      if (this.isPercentDiscount) {
        this.editForm
          .get('paymentAmount')
          ?.setValue(this.creditAmount * (1 - this.discountValue / 100), { emitEvent: false });
      } else {
        this.editForm.get('paymentAmount')?.setValue(this.creditAmount - this.discountValue, { emitEvent: false });
      }
    }
  }

  private calculateCreditAmount() {
    if (this.discountMode === DiscountMode.None) {
      this.editForm.get('creditAmount')?.setValue(this.paymentAmount, { emitEvent: false });
      return;
    }

    if (!this.isPercentDiscount) {
      this.editForm.get('creditAmount')?.setValue(this.paymentAmount + this.discountValue, { emitEvent: false });
      return;
    }

    if (this.discountMode === DiscountMode.Discount) {
      this.editForm
        .get('creditAmount')
        ?.setValue(this.paymentAmount / (1 - this.discountValue / 100), { emitEvent: false });
      return;
    }

    if (this.discountMode === DiscountMode.Additional) {
      this.editForm
        .get('creditAmount')
        ?.setValue(this.paymentAmount * (1 + this.discountValue / 100), { emitEvent: false });
      return;
    }
  }

  private updateCreditLineItem() {
    let discountValue: number | null = null;
    let discountType: InvoiceLineItemDiscountType | null = null;
    if (this.discountMode !== DiscountMode.None) {
      discountType = this.discountType;
      discountValue = this.discountValue;
    }

    if (this.creditLineItem) {
      this.creditLineItem.unitPrice = this.creditAmount;
      this.creditLineItem.discountPrice = discountValue;
      this.creditLineItem.discountType = discountType;
      this.creditLineItem.total = this.paymentAmount;
    }
  }

  onSubmit() {
    if (this.editForm.valid) {
      this.updateCreditLineItem();
      this.activeModal.close(this.creditLineItem);
    }
  }
}
